/**
 * Asset Bundle Service
 *
 * Downloads a selection of the user's assets as a single zip.
 *
 * The zip is built and streamed by the server (`/api/media/bundle`) — never in
 * the browser. A selection can contain videos of hundreds of MB, and any
 * in-browser zip (JSZip et al.) has to hold every byte in tab memory first.
 *
 * Two requests, on purpose:
 *   1. POST /api/media/bundle  — authenticated. Verifies the user owns every
 *      asset and that the selection is within the size/count caps, then returns
 *      a single-use ticket. Everything that can fail cleanly fails here, so this
 *      function can throw a real Error the UI can show.
 *   2. GET  /api/media/bundle?ticket=… — triggered as a normal browser download,
 *      so the bytes stream to disk instead of through JS memory. A navigation
 *      cannot carry an auth header, which is what the ticket is for.
 */

import { authFetch } from '../utils/authFetch';

const API_BASE = '/api/media';

interface BundleTicketResponse {
  ticket: string;
  expiresAt: string;
  fileCount: number;
  totalBytes: number;
  filename: string;
}

/** Pull the server's error message out of a failed response, whatever its shape. */
async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const text = await response.text();
    if (!text) return fallback;
    try {
      const parsed = JSON.parse(text) as { error?: string; message?: string };
      return parsed.error || parsed.message || text;
    } catch {
      // Rate limiter and proxies answer in plain text.
      return text.slice(0, 300);
    }
  } catch {
    return fallback;
  }
}

/**
 * Download the given assets as one zip.
 *
 * @param fileIds R2 keys, i.e. the `id` of the files returned by the file
 *                storage service. The server rejects the whole request if any
 *                of them is not the caller's.
 * @param opts.flowId   Optional flow the selection came from (used for the
 *                      default archive name).
 * @param opts.filename Optional archive name; `.zip` is appended if missing.
 *
 * Resolves once the browser has been handed the download — the transfer itself
 * then proceeds in the browser's download manager, outside this promise.
 * Rejects with a message suitable for showing to the user.
 */
export async function downloadAssetsZip(
  fileIds: string[],
  opts?: { flowId?: string; filename?: string }
): Promise<void> {
  if (!Array.isArray(fileIds) || fileIds.length === 0) {
    throw new Error('Select at least one asset to download');
  }

  let response: Response;
  try {
    response = await authFetch(`${API_BASE}/bundle`, {
      method: 'POST',
      body: JSON.stringify({
        fileIds,
        flowId: opts?.flowId,
        filename: opts?.filename,
      }),
    });
  } catch (err) {
    throw new Error(
      `Could not reach the server to prepare the download: ${err instanceof Error ? err.message : String(err)}`
    );
  }

  if (!response.ok) {
    const message = await readError(response, `Download failed (${response.status})`);
    if (response.status === 401) {
      throw new Error('Please sign in again to download your assets');
    }
    throw new Error(message);
  }

  const { ticket, filename } = (await response.json()) as BundleTicketResponse;
  if (!ticket) {
    throw new Error('Server did not return a download ticket');
  }

  // Same-origin navigation → the browser streams the response to disk. The
  // server also sends Content-Disposition, which wins over `download` anyway.
  const url = `${API_BASE}/bundle?ticket=${encodeURIComponent(ticket)}`;
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename || 'assets.zip';
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export default downloadAssetsZip;
